import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { PawPrint } from 'lucide-react'
import { CaptchaModal, apiClient, ApiClientError, getSupabase } from '@dogdisk/shared'

const schema = z.object({
  identifier: z.string().min(1),
  password: z.string().min(8),
})
type FormValues = z.infer<typeof schema>

type LoginResp = {
  next_step: 'ok' | '2fa_required'
  user: { user_id: string; username: string }
  access_token?: string
  refresh_token?: string
  mfa_session_id?: string
}

export function AgentLoginPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = params.get('next') ?? '/'
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [captchaOpen, setCaptchaOpen] = useState(false)
  const [pending, setPending] = useState<FormValues | null>(null)

  // captcha 模式:lazy(首次不带)+ 梯度(2026-05-28 §4)
  const [requiredCount, setRequiredCount] = useState(1)
  const submitWithCaptcha = async (vals: FormValues, captchaTokens?: string[]) => {
    setBusy(true)
    setError(null)
    try {
      const parsed = schema.parse(vals)
      const res = await apiClient.post<LoginResp>('/gapi/public/auth/login', {
        account: parsed.identifier,
        password: parsed.password,
        captcha_tokens: captchaTokens ?? [],
      })
      if (res.next_step === '2fa_required') {
        navigate(
          `/2fa?pid=${encodeURIComponent(res.mfa_session_id ?? '')}&next=${encodeURIComponent(next)}`,
        )
        return
      }
      if (res.access_token && res.refresh_token) {
        await getSupabase().auth.setSession({
          access_token: res.access_token,
          refresh_token: res.refresh_token,
        })
      }
      navigate(next)
    } catch (e) {
      if (e instanceof ApiClientError && e.code === 'CAPTCHA_REQUIRED') {
        const req = (e.details as { required_count?: number } | undefined)?.required_count ?? 1
        setRequiredCount(req)
        setPending(vals)
        setCaptchaOpen(true)
        return
      }
      if (e instanceof ApiClientError) setError(`${e.code}: ${e.message}`)
      else setError(e instanceof Error ? e.message : '登录失败')
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = (vals: FormValues) => submitWithCaptcha(vals)

  return (
    <main
      data-testid="6t7Ec"
      className="flex min-h-screen items-center justify-center px-4"
      style={{
        background:
          'linear-gradient(180deg, #FFF7ED 0%, #F6F7F8 100%)',
      }}
    >
      <div
        className="w-full max-w-[440px] rounded-xl bg-white px-10 pb-8 pt-12"
        style={{
          boxShadow:
            '0 4px 24px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Header */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2.5">
            <PawPrint size={22} strokeWidth={2.2} className="text-amber-500" />
          </div>
          <h1 className="text-[20px] font-semibold text-gray-800">代理后台</h1>
          <p className="text-sm text-gray-500">代理登录</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="identifier" className="text-[13px] font-medium text-gray-700">
              用户名
            </label>
            <input
              id="identifier"
              autoComplete="username"
              placeholder="请输入用户名"
              {...register('identifier')}
              className={[
                'h-[42px] w-full rounded-lg border bg-gray-50 px-3.5 text-sm outline-none',
                'placeholder:text-gray-400 focus:border-amber-400 focus:bg-white',
                errors.identifier ? 'border-red-300' : 'border-gray-200',
              ].join(' ')}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="text-[13px] font-medium text-gray-700">
              密码
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="请输入密码"
              {...register('password')}
              className={[
                'h-[42px] w-full rounded-lg border bg-gray-50 px-3.5 text-sm outline-none',
                'placeholder:text-gray-400 focus:border-amber-400 focus:bg-white',
                errors.password ? 'border-red-300' : 'border-gray-200',
              ].join(' ')}
            />
          </div>

          {/* captcha 走 lazy modal:连续失败后才弹;不再展示静态占位 */}

          {error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="h-11 w-full rounded-lg text-[15px] font-semibold tracking-[0.15em] text-white transition-colors disabled:opacity-70"
            style={{ backgroundColor: '#F59E0B' }}
          >
            {busy ? '登录中…' : '登 录'}
          </button>
        </form>

        <p className="mt-8 text-center text-xs text-gray-400">
          代理后台 · v1.0.0
        </p>
      </div>

      <CaptchaModal
        open={captchaOpen}
        onClose={() => setCaptchaOpen(false)}
        requiredCount={requiredCount}
        onVerified={(tokens) => {
          if (pending) void submitWithCaptcha(pending, tokens)
        }}
      />
    </main>
  )
}
